import { useEffect, useState } from "react";
import { Check, RefreshCw, Loader2, FlipHorizontal2, Rocket, Undo2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { DuckHelper } from "../components/DuckHelper";
import { ColorCountPicker } from "./ColorCountPicker";
import { PaidActionDialog } from "./PaidActionDialog";
import { ViewCheckNote } from "./ViewCheckNote";
import { ViewHoldBanner } from "./ViewHoldBanner";
import { Textarea } from "./ui/textarea";
import api, { errorText } from "../lib/api";
import { fixNote, holdCost, isWaiting, openBlocks, viewFlags } from "../lib/viewHold";

const OPPOSITE = { left: "right", right: "left" };
const VIEW_ORDER = ["back", "left", "right", "closeup"];
const VIEW_LABELS = { closeup: "face close-up" };

const ViewCard = ({ view, src, status, canMirror, canUndo, busy, onAction, held, children }) => {
  const approved = status === "approved";
  const label = VIEW_LABELS[view] || view;
  return (
    <li data-testid={`view-card-${view}`} className={`min-w-0 rounded-3xl border-2 overflow-hidden bg-card ${held ? "border-destructive/60" : approved ? "border-accent" : "border-border"}`}>
      <div className="relative">
        <img src={src} alt={`AI ${label} view`} className="w-full aspect-square object-cover" />
        <span
          data-testid={`view-status-${view}`}
          className={`absolute top-2 left-2 max-w-[calc(100%-1rem)] truncate whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-mono2 uppercase tracking-wide ${
            approved ? "bg-accent text-accent-foreground" : "bg-background/90 text-muted-foreground"
          }`}
        >
          {label} · {approved ? "approved" : "review"}
        </span>
      </div>
      <div className="p-2 grid gap-2">
        {approved ? (
          <Button data-testid={`view-unapprove-${view}`} variant="outline" disabled={!!busy} onClick={() => onAction("unapprove")} className="rounded-full h-10 gap-1.5 px-2 border-accent/50 text-accent hover:text-accent">
            {busy === "unapprove" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Undo2 className="h-4 w-4 shrink-0" />} Unapprove
          </Button>
        ) : (
          <Button data-testid={`view-approve-${view}`} disabled={!!busy} onClick={() => onAction("approve")} className="rounded-full h-10 gap-1.5 px-2 font-bold">
            <Check className="h-4 w-4 shrink-0" /> Approve
          </Button>
        )}
        <Button data-testid={`view-redo-${view}`} variant="outline" disabled={!!busy} onClick={() => onAction("redo")} className="rounded-full h-10 gap-1.5 px-2">
          {busy === "redo" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <RefreshCw className="h-4 w-4 shrink-0" />} Redo
        </Button>
        {canUndo && (
          <Button data-testid={`view-undo-${view}`} variant="ghost" disabled={!!busy} onClick={() => onAction("undo")} className="rounded-full h-10 gap-1.5 px-2 text-xs">
            {busy === "undo" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <RotateCcw className="h-4 w-4 shrink-0" />} Undo redo
          </Button>
        )}
        {canMirror && !approved && (
          <Button data-testid={`view-mirror-${view}`} variant="ghost" disabled={!!busy} onClick={() => onAction("mirror")} className="rounded-full h-10 gap-1.5 px-2 text-xs">
            <FlipHorizontal2 className="h-4 w-4 shrink-0" /> Use the {OPPOSITE[view]} view
          </Button>
        )}
      </div>
      {children}
    </li>
  );
};


// Before the sculpt: which filaments this duck needs (read from the approved renders) and how many slots to paint for.
const ColorPlan = ({ creation, onUpdate, refreshKey }) => {
  const [est, setEst] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/creations/${creation.id}/palette-estimate`).then(({ data }) => live && setEst(data)).catch(() => live && setEst(null));
    return () => { live = false; };
  }, [creation.id, refreshKey]);
  if (!est || !est.needed) return null;
  const chosen = creation.paint_opts?.colors || est.chosen || est.recommended;
  const choice = est.choices.find((c) => c.colors === chosen);
  const pick = async (colors) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/creations/${creation.id}/paint-slots`, { colors });
      onUpdate(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the colour count");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="color-plan" className="rounded-3xl border border-border bg-card/60 p-4 space-y-3">
      <p className="font-semibold">
        This duck needs {est.needed} filament colours{est.needed > 4 ? ` (${est.recommended} slots fit them all)` : ""}
      </p>
      <ul className="flex flex-wrap gap-2">
        {est.palette.map((p) => (
          <li key={p.hex + p.name} className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs">
            <span className="h-3.5 w-3.5 rounded-full border border-border" style={{ background: p.hex }} />
            {p.name}
          </li>
        ))}
      </ul>
      <ColorCountPicker title="Paint and print with" value={chosen} needed={est.needed} capacity={creation.slot_capacity} onChange={(c) => !saving && pick(c)} />
      {choice && !choice.fits && choice.merged.length > 0 && (
        <p data-testid="color-plan-merge" className="text-sm text-amber-700 dark:text-amber-400">
          With {chosen} colours, {choice.merged.join(", ")} will be painted with the nearest remaining filament.
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">Estimated from the approved views; the finished sculpt can occasionally add one small detail colour.</p>
    </div>
  );
};

// The cost-first redo confirmation; a colour-flag fix shows its pre-filled note for editing.
const RedoDialog = ({ ask, creation, busy, onChange, onCancel, onConfirm }) => (
  <PaidActionDialog
    open={!!ask}
    title={ask?.fix ? `Redo the ${VIEW_LABELS[ask.view] || ask?.view} view with this fix?` : `Redo the ${VIEW_LABELS[ask?.view] || ask?.view} view?`}
    what={ask?.fix ? "The new view is drawn with the note below. Change it if you like." : ask?.note ? "The new view is drawn with your notes." : "A new AI view replaces this one; you can undo it afterwards."}
    cost={holdCost(creation.view_check, "redo_view")}
    cancelsReview={isWaiting(creation)}
    confirmLabel="Redo"
    busy={busy}
    onCancel={onCancel}
    onConfirm={onConfirm}
  >
    {ask?.fix && (
      <Textarea data-testid="redo-fix-note" value={ask.note} onChange={(e) => onChange(e.target.value)} rows={4} maxLength={400} className="rounded-2xl text-sm" />
    )}
  </PaidActionDialog>
);

export const ViewsReview = ({ creation, onUpdate, onBack }) => {
  const [busy, setBusy] = useState({});
  const [ask, setAsk] = useState(null); // {view, note, fix}: a paid redo waiting for its cost confirmation
  const vc = creation.view_check;
  const blocks = openBlocks(vc);
  const held = Object.keys(blocks).length > 0;
  const status = creation.views_status || {};
  const views = creation.render_views || {};
  const undo = creation.views_undo || {};
  const VIEWS = VIEW_ORDER.filter((v) => views[v] != null);
  const approvedCount = VIEWS.filter((v) => status[v] === "approved").length;
  const label = (v) => VIEW_LABELS[v] || v;

  const act = async (view, action, note) => {
    setBusy((b) => ({ ...b, [view]: action }));
    try {
      const payload = action === "redo" && note ? { note } : undefined;
      const { data } = await api.post(`/creations/${creation.id}/views/${view}/${action}`, payload);
      onUpdate(data);
      if (action === "redo") toast(note ? `New ${view} view rendered with your notes — review it again` : "New " + view + " view rendered — review it again");
      if (action === "undo") toast(`Reverted the ${view} view to the previous render`);
      if (action === "unapprove") toast(`${view} view is back under review`);
    } catch (e) {
      if (e?.response?.status === 409) {
        const { data } = await api.get(`/creations/${creation.id}`);
        onUpdate(data);
      }
      toast.error(errorText(e, `Could not ${action} the ${view} view`));
    } finally {
      setBusy((b) => ({ ...b, [view]: null }));
    }
  };

  // With the hold switched on every redo shows its cost first; switched off it runs straight away, as before.
  const redo = (view, note, fix = false) => (vc?.hold ? setAsk({ view, note: note || "", fix }) : act(view, "redo", note));
  const confirmRedo = async () => {
    await act(ask.view, "redo", ask.note.trim() || undefined);
    setAsk(null);
  };

  const sculptAll = async () => {
    setBusy((b) => ({ ...b, all: "sculpt" }));
    try {
      const { data } = await api.post(`/creations/${creation.id}/sculpt`);
      onUpdate(data);
      toast.success("All views approved — sculpting your duck");
    } catch (e) {
      if (e?.response?.data?.code === "broken_views") {
        const { data } = await api.get(`/creations/${creation.id}`);
        onUpdate(data);
      }
      toast.error(errorText(e, "Could not start the sculpt"));
    } finally {
      setBusy((b) => ({ ...b, all: null }));
    }
  };

  return (
    <section data-testid="views-review" className="space-y-4">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent text-xs font-mono2 uppercase tracking-widest px-3 py-1">
          Step 3a · approve the views
        </span>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">Do the face and sides look right?</h2>
        <p className="text-muted-foreground text-sm mt-1">
          These AI views are the exact images used to sculpt your duck in 3D. Approve each one, redo any that look off, or reuse an approved side for the other side. Sculpting starts once all {VIEWS.length} are approved.
        </p>
      </div>
      {held && <ViewHoldBanner creation={creation} blocks={blocks} onUpdate={onUpdate} onBack={onBack} />}
      <ul data-testid="view-cards" className="views-grid gap-3">
        {VIEWS.map((v) => (
          <ViewCard
            key={v}
            view={v}
            src={views[v]}
            status={status[v]}
            busy={busy[v]}
            canMirror={!!OPPOSITE[v] && status[OPPOSITE[v]] === "approved"}
            canUndo={!!undo[v]}
            held={!!blocks[v]}
            onAction={(action) => (action === "redo" ? redo(v) : act(v, action))}
          >
            <ViewCheckNote view={v} block={blocks[v]} flags={viewFlags(vc, v)} busy={busy[v]} onFix={() => redo(v, fixNote(viewFlags(vc, v)), true)} />
          </ViewCard>
        ))}
      </ul>
      <DuckHelper
        creation={creation}
        applyOptions={VIEWS.filter((v) => status[v] !== "approved").map((v) => ({ id: v, label: `Redo ${label(v).charAt(0).toUpperCase() + label(v).slice(1)} with these notes` }))}
        applyBusy={VIEWS.find((v) => busy[v] === "redo")}
        onApply={(view, note) => redo(view, note)}
        emptyApplyHint="All views are approved — un-approve one to redo it with these notes."
      />
      <ColorPlan creation={creation} onUpdate={onUpdate} refreshKey={JSON.stringify(status) + Object.keys(views).map((k) => (views[k] || "").length).join(",")} />
      <Button data-testid="approve-all-sculpt-btn" onClick={sculptAll} disabled={!!busy.all || held} className="w-full h-12 rounded-full font-bold gap-2">
        {busy.all ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
        {held ? "Start sculpting is on hold" : approvedCount === VIEWS.length ? "Start sculpting" : `Approve ${approvedCount ? "the rest" : "all"} & sculpt`}
      </Button>
      <RedoDialog ask={ask} creation={creation} busy={!!ask && busy[ask.view] === "redo"} onChange={(note) => setAsk((a) => ({ ...a, note }))} onCancel={() => setAsk(null)} onConfirm={confirmRedo} />
    </section>
  );
};
